import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: BRANCH_PROTECTION.
 *
 * Vendor-agnostic representation of "rules that apply to pushes/merges on a
 * protected branch." Covers GitHub branch protection rules, GitLab protected
 * branch + push rule settings, Bitbucket branch restrictions.
 *
 * Captures the audit-relevant facts the SOC 2 CC8.1 / change-management
 * checks care about: review enforcement, status checks required, admin
 * enforceability, force-push/delete safety.
 */

export const BRANCH_PROTECTION_SCHEMA_VERSION = 1;

export const BranchProtection = z.object({
  /** Branch name pattern (e.g. "main", "release/*"). */
  branchPattern: z.string(),

  /** Number of approving reviews required to merge. 0 = no review required. */
  requiredApprovingReviews: z.number().int().min(0),
  /** Dismiss stale reviews when new commits are pushed. */
  dismissStaleReviews: z.boolean(),
  /** Require review from a code owner. */
  requireCodeOwnerReview: z.boolean(),

  /** Status checks that must pass before merge. */
  requiredStatusCheckContexts: z.array(z.string()),
  /** Require branches to be up-to-date with base before merging. */
  requireUpToDateBeforeMerge: z.boolean(),

  /** Admins are bound by the same rules (no override). */
  enforceForAdmins: z.boolean(),

  /** Signed commits required. */
  requireSignedCommits: z.boolean(),
  /** Linear history required (no merge commits). */
  requireLinearHistory: z.boolean(),
  /** Conversation resolution required before merge. */
  requireConversationResolution: z.boolean(),

  /** Force-pushes allowed (true = weaker posture). */
  allowForcePushes: z.boolean(),
  /** Branch deletion allowed (true = weaker posture). */
  allowDeletions: z.boolean(),
});

export type BranchProtection = z.infer<typeof BranchProtection>;
